'use client'
import * as React from 'react'
import { useTranslations } from 'next-intl'
import { cubicBezier, motion, useTransform, AnimatePresence } from 'framer-motion'
import { useConstant } from 'hooks/use-constant'
import { useLinearProgress } from 'hooks/use-linear-progress'
import { useUserCoinBalanceQuery } from 'codegen/generated/coins'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUserAuthModal } from 'components/user-auth-dialog/user-auth-dialog-context'
import { useNotEnoughCoinsDialog } from '@repo/web-ui/ask-cf/contexts/not-enough-coins-dialog-context'
import { randomBetween } from 'utils/math'

import { CoinIcon, StarCircleTipIcon } from './../../../../../../assets/icons'
import { useClubhousesGiveTipToDiscussionMutation } from '@web/gen/clubhouses'
import { useDiscussion } from '../_hooks/use-discussion'

export function TipButton({
  discussionId,
  discussionSlug,
  withText = false,
}: {
  discussionId: string
  discussionSlug: string
  withText?: boolean
}) {
  const t = useTranslations('pages.clubhouse.discussions')
  const spread = 350
  const duration = 1250
  const [coins, setCoins] = React.useState<number[]>([])
  // eslint-disable-next-line cf-web/codegen-query-patterns
  const userBalance = useUserCoinBalanceQuery()
  const isLoggedInQuery = useIsLoggedInQuery()
  const { isPending, mutate: giveTipToDiscussion } = useClubhousesGiveTipToDiscussionMutation()
  const { openModal } = useUserAuthModal()
  const { openDialog } = useNotEnoughCoinsDialog()
  const { refreshDiscussion } = useDiscussion(discussionSlug)
  function onAnimate() {
    const next = [
      randomBetween(0, 0.1),
      randomBetween(0, 0.2),
      ...Array(5 - 2)
        .fill(0)
        .map(() => randomBetween(0, spread)),
    ]

    setCoins(coins => [...coins, ...next])

    setTimeout(() => {
      setCoins(coins => coins.filter(coin => !next.includes(coin)))
    }, duration + spread)
  }

  const balance = React.useMemo(
    () => userBalance.data?.userBalance.balance || 0,
    [userBalance.data?.userBalance.balance],
  )

  const notEnoughCoins = Boolean(balance < 5)

  function handleClickGiveTip() {
    if (!isLoggedInQuery.isLoading && !isLoggedInQuery.data) {
      openModal('register')
      return
    } else if (notEnoughCoins) {
      openDialog()
    } else {
      onAnimate()
      giveTipToDiscussion(
        { input: { discussionId: discussionId } },
        {
          onSuccess: () => {
            refreshDiscussion()
          },
        },
      )
    }
  }

  const isDisabledButton = isLoggedInQuery.isLoading || isPending

  const buttonOrDiv = withText ? (
    <div
      onClick={handleClickGiveTip}
      className="pointer-events-auto flex flex-row items-center cursor-pointer justify-start p-2 gap-2 hover:bg-cf-grey-100 rounded relative w-full"
    >
      <StarCircleTipIcon className="size-5 " />
      <div className="text-14 font-medium">{t('give_coins')}</div>
    </div>
  ) : (
    // oxlint-disable-next-line react/button-has-type
    <button
      className="text-cf-gray-800 pointer-events-auto !cursor-pointer !disabled:cursor-pointer text-14 hover:text-legacy-cf-purple-100 tablet:gap-1.5 flex items-center gap-0 p-0 xl:flex-row"
      onClick={handleClickGiveTip}
      aria-label={t('label')}
      disabled={isDisabledButton}
    >
      <StarCircleTipIcon className="size-5 " />
      {withText ? <div className="text-14 font-medium">{t('give_coins')}</div> : null}
    </button>
  )

  return (
    <div className="tablet:inline-block !cursor-pointer flex items-center w-full">
      <div className="text-cf-sf-gray-300 relative z-[100]">
        <AnimatePresence>
          {coins.map(delay => (
            <CoinsAnimation key={delay} delay={delay} duration={duration} />
          ))}
        </AnimatePresence>
      </div>
      {buttonOrDiv}
    </div>
  )
}

export function CoinsAnimation({ delay, duration }: { delay: number; duration: number }) {
  const offsetX = useConstant(() => randomBetween(-150, 150))
  const rotateXTo = useConstant(() => randomBetween(-360, 360))
  const bottom = useConstant(() => randomBetween(20, 60))

  const values = useLinearProgress({
    duration,
    from: 0,
    to: 1,
    delay,
  })

  const eased = useTransform(values.value, cubicBezier(0, 1, 0.5, 2))
  const y = useTransform(eased, value => -bottom * value)
  const x = useTransform(values.value, value => `${value * offsetX}%`)
  const opacity = useTransform(values.value, [0, 0.1, 0.6, 1], [0, 1, 1, 0])
  const rotateZ = useTransform(values.value, value => `${value * rotateXTo}deg`)

  return (
    <motion.div
      className="absolute left-[40%] top-0 z-[100] origin-center saturate-200"
      style={{ x, y, opacity, rotateZ }}
    >
      <CoinIcon className="text-cf-sf-gray-300" />
    </motion.div>
  )
}
